import { t } from "@lingui/macro";
import { ShowMore } from "@re-dev/react-truncate";
import cx from "classnames";
import { format, formatDistance, formatDistanceToNowStrict, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Field, Form, Formik } from "formik";
import * as React from "react";
import ReactMarkdown from "react-markdown";
import { useDispatch, useSelector } from "react-redux";
import { File } from "../../../components/Form/Formik/File";
import { LinkedinEducation, LinkedinLanguage, LinkedinResume } from "../../../interfaces/resources/linkedinResume";
import { Talent } from "../../../interfaces/resources/talent";
import { createLinkedinResumeAction } from "../../../services/api/talent/linkedInProfile/actions";
import globalStyles from "../../../styles/global.module.scss";
import styles from "./index.module.scss";

export const LinkedinResumeData: React.FC = () => {
  const talent: Talent | null = useSelector((state: any) => state.talent);
  const [linkedinResume, setLinkedinResume] = React.useState<LinkedinResume | null>(null);
  const [restructuredLinkedinResume, setRestructuredLinkedinResume] = React.useState<any>(null);
  const [showAllExperiences, setShowAllExperiences] = React.useState(false);
  const [showAllEducations, setShowAllEducations] = React.useState(false);
  const dispatch = useDispatch();
  const addLinkedinResume = (iri: string, file: string) => dispatch(createLinkedinResumeAction(iri, file));

  const date = linkedinResume?.updatedAt && format(parseISO(linkedinResume.updatedAt), "dd/MM/yyyy");
  const initialValues = {
    documentFile: undefined,
  };
  const submitForm = (values: any) => {
    if (!!talent) {
      addLinkedinResume(talent["@id"], values);
    }
  };

  React.useEffect(() => {
    if (talent && talent.lastLinkedinResume) {
      setLinkedinResume(talent.lastLinkedinResume);
    }
  }, [talent]);

  interface Experience {
    companyName: string;
    jobTitle: string;
    startDate?: string | undefined; // ISO date string
    endDate?: string | undefined; // ISO date string or null
    description?: string | undefined;
    city?: string | undefined;
    company: {
      imageUrl?: string;
    } | null;
    jobs?: JobEntry[];
  }

  interface JobEntry {
    jobTitle: string;
    description: string | undefined;
    startDate: string | undefined;
    endDate: string | undefined;
    city: string | undefined;
  }

  interface CompanyEntry {
    companyName: string;
    company: {
      imageUrl: string | null;
    } | null;
    startDate: string | undefined; // earliest startDate among jobs
    city: string | undefined;
    jobs: JobEntry[];
  }
  function groupExperiencesByCompany(experiences: Experience[]): CompanyEntry[] {
    const companyMap = new Map<string, CompanyEntry>();

    experiences.forEach((experience) => {
      const { companyName, jobTitle, startDate, endDate, description, city, company } = experience;

      // Skip if companyName is not defined
      if (!companyName) {
        return;
      }

      // Prepare the job entry
      const jobEntry: JobEntry = {
        jobTitle,
        description,
        startDate,
        endDate,
        city,
      };

      // Check if the company is already in the map
      if (companyMap.has(companyName)) {
        const companyEntry = companyMap.get(companyName)!;

        // Update the earliest startDate if necessary
        if (
          startDate
          && companyEntry.startDate
          && new Date(startDate).getTime() < new Date(companyEntry.startDate).getTime()
        ) {
          companyEntry.startDate = startDate;
        } else if (!companyEntry.startDate) {
          companyEntry.startDate = startDate;
        }

        // Add the job to the jobs array
        companyEntry.jobs.push(jobEntry);
      } else {
        // Create a new company entry
        const companyEntry: CompanyEntry = {
          companyName,
          company: company ? { imageUrl: company.imageUrl || null } : null,
          startDate: startDate || undefined,
          city: city || undefined,
          jobs: [jobEntry],
        };
        companyMap.set(companyName, companyEntry);
      }
    });

    // Now, for each company, sort the jobs array by startDate descending
    const companyEntries = Array.from(companyMap.values());

    companyEntries.forEach((companyEntry) => {
      companyEntry.jobs.sort((a, b) => {
        const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
        const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
        return dateB - dateA;
      });
    });

    // Optional: Sort companies by their earliest startDate (newest first)
    companyEntries.sort((a, b) => {
      const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
      const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
      return dateB - dateA;
    });

    return companyEntries;
  }

  function processText(text: string): any {
    // Step 1: Insert double line breaks after every dot.
    text = text.replace(/\./g, ".\n\n");

    // Step 2: Split the text into lines for processing.
    const lines = text.split("\n");

    const elements: React.ReactNode[] = [];
    let inList = false;
    let listItems: React.ReactNode[] = [];

    for (let line of lines) {
      line = line.trim();

      if (line === "") {
        continue; // Skip empty lines
      }

      if (line === ":-") {
        // Start of a list
        inList = true;
        listItems = [];
      } else if (inList && line.startsWith("-")) {
        // List item
        const itemText = line.substring(1).trim(); // Remove the '-' and trim
        listItems.push(<li key={listItems.length}>{itemText}</li>);
      } else {
        // If we were in a list, close it before processing the new line
        if (inList && listItems.length > 0) {
          elements.push(<ul key={elements.length}>{listItems}</ul>);
          inList = false;
          listItems = [];
        }
        // Add the line as a paragraph
        elements.push(
          <p key={elements.length} className={styles.t14}>
            {line}
          </p>,
        );
      }
    }

    // After processing all lines, if still in a list, close it
    if (inList && listItems.length > 0) {
      elements.push(<ul key={elements.length}>{listItems}</ul>);
    }

    return elements;
  }

  const formatXpDate = (startDate: string, endDate: string | undefined): string => {
    if (!endDate) {
      return formatDistanceToNowStrict(new Date(startDate), { locale: fr });
    } else {
      return formatDistance(new Date(startDate), new Date(endDate), { locale: fr });
    }
  };

  React.useEffect(() => {
    if (linkedinResume) {
      setRestructuredLinkedinResume(groupExperiencesByCompany(linkedinResume.experiences));
    }
  }, [linkedinResume]);

  const totalExperiences = restructuredLinkedinResume?.length ?? 0;
  const experiencesToShow = showAllExperiences
    ? restructuredLinkedinResume
    : restructuredLinkedinResume?.slice(0, 4) ?? [];

  const totalEducations = linkedinResume?.educations?.length ?? 0;
  const educationsToShow = showAllEducations
    ? linkedinResume?.educations
    : linkedinResume?.educations?.slice(0, 2) ?? [];
  return (
    <div className={cx("blockContainer", styles.quizAnswerContainer)}>
      <div className={styles.header}>
        <div className={styles.titleDateContainer}>
          <h2 className={styles.title}>
            <ReactMarkdown
              components={{
                p: React.Fragment,
              }}
            >
              {t`LinkedInResume.title`}
            </ReactMarkdown>
          </h2>
          {date ? <span>Importé le : {date}</span> : null}
        </div>
        <div>
          <Formik initialValues={initialValues} onSubmit={submitForm}>
            {({ values }): JSX.Element => (
              <Form>
                <div className={styles.uploadFormContent}>
                  <div>
                    <Field
                      accept="application/pdf"
                      component={File}
                      documentUrl={talent && talent.documentFile}
                      name="documentFile"
                      className={styles.fileInput}
                    />
                  </div>
                  <div>
                    <button
                      type="submit"
                      className={cx(globalStyles.cta, globalStyles.primaryCta)}
                      disabled={!!!values.documentFile}
                    >
                      Importer le cv
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      {linkedinResume ? (
        <div>
          {/* Résumé */}
          {linkedinResume.summary ? (
            <section className={styles.section}>
              <div className={styles.infoContainer}>
                <div className={styles.sectionTitle}>
                  <div className={styles.leftContainer}>
                    <div className={styles.headingContainer}>
                      <h2 className={styles.partTitle}>Infos</h2>
                    </div>
                  </div>
                </div>
                <div className={styles.textContainer}>
                  <div className={styles.textContent}>{processText(linkedinResume.summary)}</div>
                </div>
              </div>
            </section>
          ) : null}

          {/* Experiences */}
          <section className={styles.section}>
            <div className={styles.infoContainer}>
              <div className={styles.sectionTitle}>
                <div className={styles.leftContainer}>
                  <div className={styles.headingContainer}>
                    <h2 className={(styles.subPartTitle, styles.partTitle)}>Expérience</h2>
                  </div>
                </div>
              </div>
              <div>
                <ul className={styles.listContainer}>
                  {experiencesToShow?.map((experience: Experience) => (
                    <li key={experience.companyName} className={styles.listItem}>
                      <div className={styles.listItemContent}>
                        <div className={styles.companyLogoContainer}>
                          <div>
                            {experience.company && experience.company.imageUrl ? (
                              <img width="48" src={experience.company.imageUrl} alt="" />
                            ) : (
                              <img width="48" src="/LinkedinTombstoneLogo.png" alt="" />
                            )}
                          </div>
                        </div>
                        <div className={styles.experienceData}>
                          <div className={styles.experienceHeader}>
                            <div className={styles.itemTitle}>
                              {experience.jobs && experience.jobs.length > 1 ? (
                                <span className={styles.subPartTitle}>{experience.companyName}</span>
                              ) : (
                                <span className={styles.subPartTitle}>{experience.jobs?.[0].jobTitle}</span>
                              )}
                            </div>
                            <div>
                              {experience.jobs && experience.jobs.length === 1 ? (
                                <span className={styles.t14}>{experience.companyName}</span>
                              ) : experience.startDate
                                && experience.jobs
                                && experience.jobs.length > 1
                                && experience.jobs[0].endDate ? (
                                <span className={styles.t14}>
                                  {formatXpDate(experience.startDate, experience.jobs[0].endDate)}
                                </span>
                              ) : experience.startDate
                                && experience.jobs
                                && experience.jobs.length > 1
                                && !experience.jobs[0].endDate ? (
                                <span className={styles.t14}>
                                  {formatXpDate(experience.startDate, experience.endDate)}
                                </span>
                              ) : null}
                            </div>
                            <div>
                              {experience.jobs && experience.jobs.length === 1 ? (
                                <span className={cx(styles.t14, styles.cGrey)}>
                                  {experience.jobs[0].startDate
                                    ? format(parseISO(experience.jobs[0].startDate), "MMMM yyyy", { locale: fr })
                                    : null}
                                  {" - "}
                                  {experience.jobs[0].endDate
                                    ? format(parseISO(experience.jobs[0].endDate), "MMMM yyyy", { locale: fr })
                                    : ""}
                                  {" - "}
                                  {experience.jobs[0].startDate
                                    ? formatXpDate(experience.jobs[0].startDate, experience.jobs[0].endDate)
                                    : ""}
                                </span>
                              ) : null}
                            </div>
                            <span className={cx(styles.t14, styles.cGrey)}>
                              {experience.city ? experience.city : null}
                            </span>
                          </div>
                          <div className={styles.experienceDetail}>
                            <ul>
                              {experience.jobs?.map((job: JobEntry) => (
                                <li key={job.jobTitle} className={styles.jobList}>
                                  {experience.jobs && experience.jobs.length > 1 ? (
                                    <>
                                      <span className={styles.dot}></span>
                                      <div
                                        className={cx(
                                          job.jobTitle === experience.jobs[0].jobTitle
                                            ? styles.jobData
                                            : styles.jobDataSecond,
                                        )}
                                      >
                                        <div className={styles.jobHeader}>
                                          <div className={styles.jobTitle}>
                                            <span className={styles.subPartTitle}>{job.jobTitle}</span>
                                          </div>
                                          <div>
                                            {job.startDate ? (
                                              <span className={cx(styles.t14, styles.cGrey)}>
                                                {formatXpDate(job.startDate, job.endDate)}
                                              </span>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className={styles.jobDesc}>
                                          {job.description ? (
                                            <div className={styles.t14}>
                                              <ShowMore more="voir plus" lines={2} less="réduire">
                                                {processText(job.description)}
                                              </ShowMore>
                                            </div>
                                          ) : null}
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <div className={styles.jobDesc}>
                                      {job.description ? (
                                        <div className={styles.t14}>
                                          <ShowMore more="voir plus" lines={2} less="réduire">
                                            {processText(job.description)}
                                          </ShowMore>
                                        </div>
                                      ) : null}
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            {totalExperiences > 4 && !showAllExperiences && (
              <button className={styles.showMoreButton} onClick={() => setShowAllExperiences(true)}>
                Afficher les {totalExperiences} expériences
              </button>
            )}
          </section>
          {/* Formation */}
          <section className={styles.section}>
            <div className={styles.infoContainer}>
              <div className={styles.sectionTitle}>
                <div className={styles.leftContainer}>
                  <div className={styles.headingContainer}>
                    <h2 className={(styles.subPartTitle, styles.partTitle)}>Formation</h2>
                  </div>
                </div>
              </div>
              <ul className={styles.listContainer}>
                {educationsToShow?.map((education: LinkedinEducation) => (
                  <li key={education["@id"]} className={styles.listItem}>
                    <div className={styles.listItemContent}>
                      <div className={styles.companyLogoContainer}>
                        <div>
                          {education.imageUrl ? (
                            <img width="48" src={education.imageUrl} alt="" />
                          ) : (
                            <img width="48" src="/LinkedinTombstoneLogo.png" alt="" />
                          )}
                        </div>
                      </div>
                      <div className={styles.experienceData}>
                        <div className={styles.experienceHeader}>
                          <div className={styles.itemTitle}>
                            <span className={styles.subPartTitle}>{education.schoolName}</span>
                          </div>
                          <div>
                            <span className={styles.t14}>{education.degreeName}</span>
                          </div>
                          <div>
                            <span className={cx(styles.t14, styles.cGrey)}>
                              {education.startDate
                                ? format(parseISO(education.startDate), "MMMM yyyy", { locale: fr })
                                : ""}
                              {" - "}
                              {education.endDate
                                ? format(parseISO(education.endDate), "MMMM yyyy", { locale: fr })
                                : ""}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            {totalEducations > 4 && !showAllEducations && (
              <button className={styles.showMoreButton} onClick={() => setShowAllEducations(true)}>
                Afficher les {totalEducations} formations
              </button>
            )}
          </section>
          {/* Langues */}
          {linkedinResume.languages && linkedinResume.languages.length > 0 ? (
            <section className={styles.section}>
              <div className={styles.infoContainer}>
                <div className={styles.sectionTitle}>
                  <div className={styles.leftContainer}>
                    <div className={styles.headingContainer}>
                      <h2 className={(styles.subPartTitle, styles.partTitle)}>Langues</h2>
                    </div>
                  </div>
                </div>
                <ul className={styles.listContainer}>
                  {linkedinResume?.languages?.map((language: LinkedinLanguage) => (
                    <li key={language["@id"]} className={styles.listItem}>
                      <div className={styles.listItemContent}>
                        <div className={styles.experienceData}>
                          <div className={styles.experienceHeader}>
                            <div className={styles.itemTitle}>
                              <span className={styles.subPartTitle}>{language.name}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
